import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Globe, Smartphone, Monitor, MonitorSmartphone } from 'lucide-react';
// Use new unified product configuration
import { getProductsByCategory, getProductStyle, get12MonthProductId } from '../../config/homeProductsConfig';
// Import custom user icons
import { UserCountBadge, UserCountDisplay } from '../icons/UsersIcon';

export default function ProductGrid() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  
  // 从配置中获取各类别产品
  const officialProducts = getProductsByCategory('official');
  const officialRenewalProducts = getProductsByCategory('official-renewal');
  const officialSharedProducts = getProductsByCategory('official-shared');
  const sharedProducts = getProductsByCategory('shared');
  const monkeyaiProducts = getProductsByCategory('monkeyai');

  // 获取图标组件
  const getIconComponent = (iconName) => {
    const iconMap = {
      'Globe': Globe,
      'Smartphone': Smartphone
    };
    return iconMap[iconName] || Globe;
  };

  const handleProductClick = (product) => {
    // 获取对应的12个月套餐产品ID
    const twelveMonthProductId = get12MonthProductId(product.productId);
    console.log('Product clicked, navigating to product details page:', {
      productID: product.productId,
      productType: product.type,
      twelveMonthProductId: twelveMonthProductId,
      redirectURL: `/product/${twelveMonthProductId}?type=${product.type}`
    });
    navigate(`/product/${twelveMonthProductId}?type=${product.type}`);
  };



  // 渲染产品列表的函数
  const renderProductList = (products, categoryName, categoryDescription, categoryType) => {
    if (products.length === 0) return null;

    return (
      <div className="mb-8 sm:mb-12 lg:mb-16">
        {/* 大类标题 - 响应式优化 */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{categoryName}</h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-4">{categoryDescription}</p>
          
          
          <div className="w-16 sm:w-20 lg:w-24 h-1 bg-orange-500 mt-4 sm:mt-6 rounded-full"></div>
        </div>


        {/* 产品列表 */}
        <div className="space-y-3 sm:space-y-4">
          {products.map((product, index) => {
            const productStyle = getProductStyle(product.type);
            const IconComponent = getIconComponent(productStyle.icon);
            
            // 应用Sunset Glow Card System的背景设计
            const categoryBackgrounds = {
              'official': 'from-blue-50 via-white to-indigo-50',
              'official-renewal': 'from-emerald-50 via-white to-green-50',
              'official-shared': 'from-orange-50 via-white to-amber-50', 
              'shared': 'from-purple-50 via-white to-pink-50',
              'monkeyai': 'from-green-50 via-white to-teal-50'
            };
            const bgGradient = categoryBackgrounds[categoryType] || 'from-orange-50 via-white to-amber-50';
            
          return (
            <div
              key={product.id}
              className={`bg-gradient-to-br ${bgGradient} rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 border border-orange-200 group cursor-pointer relative`}
              onClick={() => handleProductClick(product)}
            >
              {/* 装饰性背景光晕 */}
              <div className="absolute -top-10 -right-10 w-20 h-20 bg-orange-200 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-10 -left-10 w-20 h-20 bg-amber-200 rounded-full opacity-20 blur-2xl"></div>
              <div className="p-4 sm:p-6 relative z-10">
                
                {/* 桌面端表格布局 */}
                <div className="hidden xl:grid xl:grid-cols-10 gap-4 items-center">
                  
                  {/* 第1列：产品图片和名称 */}
                  <div className="xl:col-span-3 flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                        <img 
                          src={product.image}
                          alt={t(product.nameKey)}
                          className="w-full h-full object-cover rounded-lg group-hover:scale-105 transition-all duration-500 shadow-md"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full bg-white rounded-lg flex items-center justify-center group-hover:scale-105 transition-all duration-500 shadow-md" style={{display: 'none'}}>
                          <IconComponent className="w-8 h-8 text-gray-800" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 group-hover:text-orange-500 transition-colors duration-300 text-sm sm:text-base">
                        {t(product.nameKey)}
                      </h3>
                      <div className="text-xs text-gray-500 mt-1">
                        {product.features.loginType === 'shared' ? 
                          (product.id === 'gpt-vip' ? t('homepage.productGrid.vipExclusive') : t('homepage.productGrid.privateConversation')) : 
                          (product.features.users === 1 ? 
                            t('homepage.productGrid.userCount.single') : 
                            t('homepage.productGrid.userCount.shared', { count: product.features.users })
                          )
                        }
                      </div>
                    </div>
                  </div>

                  {/* 第2列：价格对比 - Sunset Glow 风格 */}
                  <div className="xl:col-span-1 text-center">
                    <div className="flex flex-col items-center">
                      <div className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                        {product.currency}{product.price}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <span className="line-through text-gray-400">{product.currency}{product.originalPrice}</span>
                        <span className="ml-1">{t('homepage.productGrid.perMonth')}</span>
                      </div>
                    </div>
                  </div>

                  {/* 第3列：使用人数 */}
                  <div className="xl:col-span-1 text-center">
                    <div className="flex justify-center">
                      <UserCountBadge count={product.features.users} size="sm" />
                    </div>
                  </div>

                  {/* 第4列：登录方式 */}
                  <div className="xl:col-span-2 text-center">
                    <div className="text-sm text-blue-600 font-medium">
                      {product.features.loginType === 'official' && t('homepage.productGrid.loginTypes.official')}
                      {product.features.loginType === 'shared' && t('homepage.productGrid.loginTypes.shared')}
                      {product.features.loginType === 'custom' && t('homepage.productGrid.loginTypes.custom')}
                    </div>
                    <div className="text-xs text-gray-500">{t('homepage.productGrid.tableHeaders.loginType')}</div>
                  </div>

                  {/* 第5列：支持环境 */}
                  <div className="xl:col-span-1 text-center">
                    {/* 官网账号是网页+APP，其他都是仅网页 */}
                    {product.features.loginType === 'official' ? (
                      <>
                        <MonitorSmartphone className="w-5 h-5 text-green-500 mx-auto mb-1" />
                        <div className="text-xs text-green-600">{t('homepage.productGrid.platform.webAndApp')}</div>
                      </>
                    ) : (
                      <>
                        <Monitor className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                        <div className="text-xs text-blue-600">{t('homepage.productGrid.platform.webOnly')}</div>
                      </>
                    )}
                  </div>

                  {/* 第6列：操作按钮 - Sunset Glow Button */}
                  <div className="xl:col-span-2 text-center">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(product);
                      }}
                      className="relative bg-gradient-to-r from-orange-500 via-orange-600 to-red-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 mx-auto group overflow-hidden hover:scale-105"
                    >
                      {/* 动态背景效果 */}
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      <div className="relative z-10 flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
                        <span className="text-sm">
                          {product.features.loginType === 'custom' ? t('homepage.productGrid.buttons.trial') : t('homepage.productGrid.buttons.details')}
                        </span>
                      </div>
                      
                      {/* 闪光效果 */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 via-red-400 to-orange-400 opacity-30 blur-lg group-hover:opacity-50 transition-opacity duration-300"></div>
                    </button>
                  </div>
                </div>

                {/* 移动端和平板端卡片布局 */}
                <div className="xl:hidden">
                  
                  {/* 顶部：产品信息和价格 */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <div className="relative w-12 h-12 sm:w-14 sm:h-14">
                          <img 
                            src={product.image}
                            alt={t(product.nameKey)}
                            className="w-full h-full object-cover rounded-lg group-hover:scale-105 transition-all duration-500 shadow-md"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-full bg-white rounded-lg flex items-center justify-center group-hover:scale-105 transition-all duration-500 shadow-md" style={{display: 'none'}}>
                            <IconComponent className="w-6 h-6 text-gray-800" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 group-hover:text-orange-500 transition-colors duration-300 text-sm sm:text-base">
                          {t(product.nameKey)}
                        </h3>
                        <div className="text-xs text-gray-500 mt-1">
                          {product.features.loginType === 'shared' ? 
                            (product.id === 'gpt-vip' ? t('homepage.productGrid.vipExclusive') : t('homepage.productGrid.privateConversation')) : 
                            (product.features.users === 1 ? 
                              t('homepage.productGrid.userCount.single') : 
                              t('homepage.productGrid.userCount.shared', { count: product.features.users })
                            )
                          }
                        </div>
                      </div>
                    </div>
                    
                    {/* 价格区域 */}
                    <div className="text-right">
                      <div className="text-xs text-gray-400 line-through">{product.currency}{product.originalPrice}</div>
                      <div className="text-lg font-bold text-orange-500">{product.currency}{product.price}</div>
                      <div className="text-xs text-gray-500">{t('homepage.productGrid.perMonth')}</div>
                    </div>
                  </div>

                  {/* 中部：详细信息网格 */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 text-xs">
                    
                    {/* 使用人数 */}
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-gray-500 mb-1 text-center">{t('homepage.productGrid.tableHeaders.users')}</div>
                      <div className="flex justify-center">
                        <UserCountDisplay count={product.features.users} size="sm" />
                      </div>
                    </div>

                    {/* 登录地址 */}
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-gray-500 mb-1">{t('homepage.productGrid.tableHeaders.loginType')}</div>
                      <div className="font-medium text-blue-600">
                        {product.features.loginType === 'official' && t('homepage.productGrid.loginTypes.official')}
                        {product.features.loginType === 'shared' && t('homepage.productGrid.loginTypes.shared')}
                        {product.features.loginType === 'custom' && t('homepage.productGrid.loginTypes.custom')}
                      </div>
                    </div>

                    {/* 支持环境 */}
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-gray-500 mb-1">{t('homepage.productGrid.tableHeaders.platform')}</div>
                      <div className="flex items-center gap-1">
                        {product.features.loginType === 'official' ? (
                          <>
                            <MonitorSmartphone className="w-4 h-4 text-green-500" />
                            <span className="text-green-600 font-medium">{t('homepage.productGrid.platform.webAndApp')}</span>
                          </>
                        ) : (
                          <>
                            <Monitor className="w-4 h-4 text-blue-500" />
                            <span className="text-blue-600 font-medium">{t('homepage.productGrid.platform.webOnly')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 底部：操作按钮 - Sunset Glow Button */}
                  <div className="flex justify-center">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(product);
                      }}
                      className="relative w-full bg-gradient-to-r from-orange-500 via-orange-600 to-red-500 text-white py-4 rounded-2xl font-bold shadow-2xl flex items-center justify-center gap-3 transition-all duration-300 overflow-hidden group hover:scale-105 hover:shadow-3xl transform"
                    >
                      {/* 动态背景效果 */}
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* 按钮内容 */}
                      <div className="relative z-10 flex items-center gap-3">
                        <ShoppingCart className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                        <span>{product.features.loginType === 'custom' ? t('homepage.productGrid.buttons.trial') : t('homepage.productGrid.buttons.details')}</span>
                        <span className="text-xl group-hover:translate-x-1 transition-transform duration-300">→</span>
                      </div>
                      
                      {/* 闪光效果 */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 via-red-400 to-orange-400 opacity-30 blur-lg group-hover:opacity-50 transition-opacity duration-300"></div>
                    </button>
                  </div>
                </div>

              </div>
            </div>
          );
        })}
        </div>
      </div>
    );
  };

  return (
    <section id="products-section" className="py-20 lg:py-28 bg-gradient-to-br from-orange-50/30 to-amber-50/30">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        
        {/* 产品展示 */}
        <div>
          {/* 官网账号 */}
          {renderProductList(
            officialProducts, 
            t('homepage.categories.official.name'), 
            t('homepage.categories.official.description'),
            'official'
          )}

          {/* 官网账号续费 */}
          {renderProductList(
            officialRenewalProducts, 
            t('homepage.categories.officialRenewal.name'), 
            t('homepage.categories.officialRenewal.description'),
            'official-renewal'
          )}

          {/* 官网共享账号 */}
          {renderProductList(
            officialSharedProducts, 
            t('homepage.categories.officialShared.name'), 
            t('homepage.categories.officialShared.description'),
            'official-shared'
          )}

          {/* 网页共享托管账号 */}
          {renderProductList(
            sharedProducts, 
            t('homepage.categories.shared.name'), 
            t('homepage.categories.shared.description'),
            'shared'
          )}

          {/* MonkeyAI */}
          {renderProductList(
            monkeyaiProducts, 
            t('homepage.categories.monkeyai.name'), 
            t('homepage.categories.monkeyai.description'),
            'monkeyai'
          )}

          {/* 如果所有产品都为空 */}
          {officialProducts.length === 0 && officialRenewalProducts.length === 0 && officialSharedProducts.length === 0 && sharedProducts.length === 0 && monkeyaiProducts.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShoppingCart className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.productGrid.noProducts')}</h3>
                <p className="text-gray-600">{t('homepage.productGrid.noProductsDesc')}</p>
              </div>
            </div>
          )}
        </div>

      </div>
    </section>
  );
}